import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { useEffect } from 'react';

// Hero Section with CTA
const ServiceHero = () => (
  <section className="relative min-h-[500px] flex items-center" data-testid="service-hero">
    <div 
      className="absolute inset-0 bg-cover bg-center"
      style={{
        backgroundImage: `url('https://customer-assets.emergentagent.com/job_cd4f5c4d-92f8-4312-8a77-df80f7c24853/artifacts/y7iby1hw_iStock-1428027852.jpg')`
      }}
    />
    <div className="absolute inset-0 bg-[#1a2e44]/80" />
    <div className="relative max-w-6xl mx-auto px-6 lg:px-8 py-20">
      <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 uppercase">
        Irrigation Management
      </h1>
      <p className="text-xl text-slate-300 max-w-2xl mb-8">
        Protecting your landscape investment through systematic irrigation monitoring, maintenance, and water efficiency optimization.
      </p>
      <Link
        to="/contact?intent=assessment&service=irrigation&source=service-irrigation"
        className="inline-flex items-center justify-center bg-[#1a2e44] hover:bg-[#0f1d2d] text-white text-sm font-bold py-4 px-8 uppercase tracking-wide transition-colors border border-white/20"
        data-testid="hero-cta-btn"
      >
        Request a System Assessment
        <ArrowRight className="w-4 h-4 ml-2" />
      </Link>
    </div>
  </section>
);

// What's Included Section
const IncludesSection = () => {
  const services = [
    'Irrigation system inspections and audits',
    'Controller programming and seasonal adjustments',
    'Sprinkler head repairs and replacements',
    'Drip system maintenance and repairs',
    'Leak detection and repair',
    'Rain sensor installation and maintenance'
  ];

  return (
    <section className="bg-white py-20" data-testid="includes-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-[#1a2e44] mb-4">
          What Our Irrigation Programs Include
        </h2>
        <p className="text-slate-600 mb-10 max-w-2xl">
          Our irrigation management programs ensure your system operates efficiently:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {services.map((service, index) => (
            <div key={index} className="flex items-start gap-3 p-4 bg-slate-50">
              <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">{service}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why It Matters Section
const WhyItMattersSection = () => (
  <section className="bg-[#1a2e44] py-20" data-testid="why-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <h2 className="text-3xl font-bold text-white mb-6">
        Why Irrigation Management Matters
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="bg-white/10 p-6">
          <h3 className="text-lg font-bold text-white mb-3">Protect Plant Investments</h3>
          <p className="text-slate-300 text-sm">
            Proper irrigation ensures your landscape plantings receive the water they need to thrive and survive.
          </p>
        </div>
        <div className="bg-white/10 p-6">
          <h3 className="text-lg font-bold text-white mb-3">Optimize Water Efficiency</h3>
          <p className="text-slate-300 text-sm">
            Well-managed systems reduce water waste, lower utility costs, and support environmental responsibility.
          </p>
        </div>
        <div className="bg-white/10 p-6">
          <h3 className="text-lg font-bold text-white mb-3">Prevent System Failures</h3>
          <p className="text-slate-300 text-sm">
            Regular maintenance catches small issues before they become expensive repairs or plant loss.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// How We Deliver Section
const DeliverySection = () => (
  <section className="bg-slate-50 py-20" data-testid="delivery-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <h2 className="text-3xl font-bold text-[#1a2e44] mb-10">
        Our Structured Service Process
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 border-t-4 border-t-[#1a2e44]">
          <div className="text-4xl font-bold text-[#1a2e44] mb-3">01</div>
          <h3 className="text-lg font-bold text-[#1a2e44] mb-2">System Assessment</h3>
          <p className="text-slate-600 text-sm">
            We evaluate your irrigation system and identify issues.
          </p>
        </div>
        <div className="bg-white p-6 border-t-4 border-t-[#1a2e44]">
          <div className="text-4xl font-bold text-[#1a2e44] mb-3">02</div>
          <h3 className="text-lg font-bold text-[#1a2e44] mb-2">Maintenance Program</h3>
          <p className="text-slate-600 text-sm">
            We create a schedule for inspections and seasonal adjustments.
          </p>
        </div>
        <div className="bg-white p-6 border-t-4 border-t-[#1a2e44]">
          <div className="text-4xl font-bold text-[#1a2e44] mb-3">03</div>
          <h3 className="text-lg font-bold text-[#1a2e44] mb-2">Ongoing Monitoring</h3>
          <p className="text-slate-600 text-sm">
            We perform scheduled checks and address issues proactively.
          </p>
        </div>
        <div className="bg-white p-6 border-t-4 border-t-[#1a2e44]">
          <div className="text-4xl font-bold text-[#1a2e44] mb-3">04</div>
          <h3 className="text-lg font-bold text-[#1a2e44] mb-2">Documentation</h3>
          <p className="text-slate-600 text-sm">
            Every inspection is documented with findings and recommendations.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Documentation Section
const DocumentationSection = () => (
  <section className="bg-white py-20" data-testid="documentation-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="text-3xl font-bold text-[#1a2e44] mb-6">
            Documentation & Reporting
          </h2>
          <p className="text-slate-600 mb-8">
            Our irrigation management includes detailed documentation so you always know your system's status:
          </p>
          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">Zone-by-zone inspection reports</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">Photo documentation of repairs</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">Controller settings and schedules</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">System improvement recommendations</span>
            </li>
          </ul>
        </div>
        <div className="bg-slate-50 p-8 border border-slate-200">
          <p className="text-slate-600 italic text-lg">
            "Our reporting ensures you're always informed about your irrigation system's condition and performance."
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Service Area Section
const ServiceAreaSection = () => (
  <section className="bg-slate-50 py-16" data-testid="service-area-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
      <h2 className="text-2xl font-bold text-[#1a2e44] mb-4">
        Service Area
      </h2>
      <p className="text-slate-600 max-w-2xl mx-auto">
        Serving Pasadena, Arcadia, San Marino, La Cañada Flintridge, and communities throughout Los Angeles County.
      </p>
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section className="bg-[#1a2e44] py-20" data-testid="cta-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
      <h2 className="text-3xl font-bold text-white mb-4">
        Schedule a System Assessment
      </h2>
      <p className="text-slate-400 mb-8 max-w-xl mx-auto">
        The first step is an irrigation system assessment where we evaluate your system and develop a management program.
      </p>
      <Link
        to="/contact?intent=assessment&service=irrigation&source=service-irrigation"
        className="inline-flex items-center justify-center bg-[#1a2e44] hover:bg-[#0f1d2d] text-white text-sm font-bold py-4 px-8 uppercase tracking-wide transition-colors border border-white/20"
        data-testid="cta-btn"
      >
        Request a System Assessment
        <ArrowRight className="w-4 h-4 ml-2" />
      </Link>
    </div>
  </section>
);

export default function IrrigationManagementPage() {
  useEffect(() => {
    document.title = 'Irrigation Management | Sunlight Grounds Care';
  }, []);

  return (
    <div data-testid="irrigation-management-page">
      <ServiceHero />
      <IncludesSection />
      <WhyItMattersSection />
      <DeliverySection />
      <DocumentationSection />
      <CTASection />
      <ServiceAreaSection />
    </div>
  );
}
